import { useQuery } from '@tanstack/react-query';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  CardContent,
  Chip,
  CircularProgress,
  Drawer,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { alpha, useTheme } from '@mui/material/styles';
import { useMemo, useState } from 'react';
import {
  identityVerificationDashboardApi,
  type IdentitySessionListItem,
  type IdentitySessionSortBy,
} from '../api/identityVerificationDashboardApi';
import { messageFromIdentityVerificationHttpError } from '../lib/identityVerificationHttpErrorMessage';

type StatusChipColor = 'success' | 'error' | 'warning' | 'info' | 'default';

function statusChipColor(status: string | null | undefined): StatusChipColor {
  const u = (status ?? '').trim().toUpperCase();
  if (u === 'SUCCESS' || u === 'COMPLETED' || u === 'APPROVED') return 'success';
  if (u === 'REJECTED' || u === 'REJECTED_DUPLICATE' || u === 'FAILED') return 'error';
  if (u === 'PROCESSING' || u === 'INITIATED') return 'info';
  if (u === 'MANUAL_REVIEW' || u === 'PENDING') return 'warning';
  return 'default';
}

function formatDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

function JsonBlock({ value }: { value: unknown }) {
  const text = useMemo(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return String(value);
    }
  }, [value]);
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 1.25,
        borderRadius: 1.5,
        bgcolor: 'background.default',
        border: 1,
        borderColor: 'divider',
        overflow: 'auto',
        maxHeight: 320,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: '0.78rem',
        lineHeight: 1.5,
        whiteSpace: 'pre-wrap',
        wordBreak: 'break-word',
      }}
    >
      {text}
    </Box>
  );
}

function CountTile({
  label,
  value,
  caption,
  selected,
  onClick,
}: {
  label: string;
  value: number | string;
  caption?: string;
  selected?: boolean;
  onClick?: () => void;
}) {
  const theme = useTheme();
  const inner = (
    <CardContent sx={{ py: 2 }}>
      <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 700 }}>
        {label}
      </Typography>
      <Typography variant="h3" sx={{ mt: 0.75, fontWeight: 900, lineHeight: 1.1 }}>
        {value}
      </Typography>
      {caption ? (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, lineHeight: 1.45 }}>
          {caption}
        </Typography>
      ) : null}
    </CardContent>
  );
  return (
    <Card
      variant="outlined"
      sx={{
        height: '100%',
        borderRadius: 2,
        ...(selected
          ? {
              borderColor: theme.palette.primary.main,
              boxShadow: `0 0 0 1px ${alpha(theme.palette.primary.main, 0.4)}`,
            }
          : {}),
      }}
    >
      {onClick ? <CardActionArea onClick={onClick}>{inner}</CardActionArea> : inner}
    </Card>
  );
}

const SORT_OPTIONS: Array<{ value: IdentitySessionSortBy; label: string }> = [
  { value: 'CREATED_AT_DESC', label: 'Newest first (created_at)' },
  { value: 'CREATED_AT_ASC', label: 'Oldest first (created_at)' },
  { value: 'UPDATED_AT_DESC', label: 'Recently updated' },
  { value: 'UPDATED_AT_ASC', label: 'Stalest updates' },
];

export function IdentityVerificationDashboard() {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [statusFilter, setStatusFilter] = useState<string | undefined>(undefined);
  const [routeFilter, setRouteFilter] = useState<string | undefined>(undefined);
  const [vendorFilter, setVendorFilter] = useState<string | undefined>(undefined);
  const [sortBy, setSortBy] = useState<IdentitySessionSortBy>('CREATED_AT_DESC');
  const [ivIdQ, setIvIdQ] = useState('');
  const [internalIdQ, setInternalIdQ] = useState('');
  const [ccidQ, setCcidQ] = useState('');
  const [emailQ, setEmailQ] = useState('');
  const [phoneQ, setPhoneQ] = useState('');
  const [createdFrom, setCreatedFrom] = useState('');
  const [createdTo, setCreatedTo] = useState('');
  const [applied, setApplied] = useState({
    ivVerificationIdContains: '',
    internalIdContains: '',
    ccidContains: '',
    emailContains: '',
    phoneContains: '',
    createdFromIso: '',
    createdToIso: '',
  });
  const [detailId, setDetailId] = useState<string | null>(null);

  const summaryQ = useQuery({
    queryKey: ['identityVerificationDashboardSummary'],
    queryFn: () => identityVerificationDashboardApi.summary(),
  });

  const listQ = useQuery({
    queryKey: [
      'identityVerificationDashboardList',
      page,
      rowsPerPage,
      statusFilter,
      routeFilter,
      vendorFilter,
      sortBy,
      applied,
    ],
    queryFn: () =>
      identityVerificationDashboardApi.list({
        limit: rowsPerPage,
        offset: page * rowsPerPage,
        status: statusFilter,
        verification_route: routeFilter,
        vendor: vendorFilter,
        ivVerificationIdContains: applied.ivVerificationIdContains || undefined,
        internalIdContains: applied.internalIdContains || undefined,
        ccidContains: applied.ccidContains || undefined,
        emailContains: applied.emailContains || undefined,
        phoneContains: applied.phoneContains || undefined,
        createdFrom: applied.createdFromIso || undefined,
        createdTo: applied.createdToIso || undefined,
        sortBy,
      }),
  });

  const detailQ = useQuery({
    queryKey: ['identityVerificationDashboardDetail', detailId],
    queryFn: () => identityVerificationDashboardApi.detail(detailId!),
    enabled: !!detailId,
  });

  const applySearch = () => {
    setPage(0);
    setApplied({
      ivVerificationIdContains: ivIdQ.trim(),
      internalIdContains: internalIdQ.trim(),
      ccidContains: ccidQ.trim(),
      emailContains: emailQ.trim(),
      phoneContains: phoneQ.trim(),
      createdFromIso: createdFrom.trim(),
      createdToIso: createdTo.trim(),
    });
  };

  const sessionStatusEntries = useMemo(() => {
    const counts = summaryQ.data?.sessionStatusCounts ?? {};
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [summaryQ.data?.sessionStatusCounts]);

  const routeEntries = useMemo(() => {
    const counts = summaryQ.data?.verificationRouteCounts ?? {};
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [summaryQ.data?.verificationRouteCounts]);

  const vendorEntries = useMemo(() => {
    const counts = summaryQ.data?.vendorCounts ?? {};
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [summaryQ.data?.vendorCounts]);

  const dedupEntries = useMemo(() => {
    const counts = summaryQ.data?.deduplicationStatusCounts ?? {};
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [summaryQ.data?.deduplicationStatusCounts]);

  const customerEntries = useMemo(() => {
    const counts = summaryQ.data?.customerStatusCounts ?? {};
    return Object.entries(counts).sort((a, b) => b[1] - a[1]);
  }, [summaryQ.data?.customerStatusCounts]);

  return (
    <Box>
      <Accordion
        defaultExpanded
        sx={{ mb: 2, borderRadius: 2, '&:before': { display: 'none' }, border: 1, borderColor: 'divider' }}
      >
        <AccordionSummary expandIcon={<ExpandMoreRoundedIcon />} sx={{ minHeight: 56 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <InfoOutlinedIcon color="primary" fontSize="small" />
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              How identity verification runs (sessions, dedup, customers)
            </Typography>
          </Stack>
        </AccordionSummary>
        <AccordionDetails sx={{ pt: 0 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 900, lineHeight: 1.65 }}>
            Each <strong>identity_verification_session</strong> row represents one IDV attempt — either a vendor-backed
            ID + selfie upload (<code>VENDOR_UPLOAD</code>) or a returning-borrower selfie 1:1 against the stored
            biometric (<code>RETURNING_SELFIE_1TO1</code>). After Stage 1 succeeds, a Stage 2{' '}
            <strong>deduplication_verification_sessions</strong> row records the multi-tier dedup outcome and, on
            success, links a <strong>customers</strong> profile (CCID). Operators can filter the table below by status,
            route, vendor, CCID, email, phone, or any of the IDs.
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                    Session statuses
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {Object.entries(summaryQ.data?.statusLegend ?? {}).map(([k, v]) => (
                      <Typography key={k} variant="caption" color="text.secondary" component="div">
                        <strong>{k}:</strong> {v}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="overline" color="primary" sx={{ fontWeight: 800 }}>
                    Verification routes
                  </Typography>
                  <Stack spacing={0.75} sx={{ mt: 1 }}>
                    {Object.entries(summaryQ.data?.routeLegend ?? {}).map(([k, v]) => (
                      <Typography key={k} variant="caption" color="text.secondary" component="div">
                        <strong>{k}:</strong> {v}
                      </Typography>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>

      <Stack direction="row" alignItems="baseline" justifyContent="space-between" flexWrap="wrap" gap={1} sx={{ mb: 2 }}>
        <Typography
          variant="h5"
          sx={{
            fontWeight: 800,
            letterSpacing: -0.02,
            background: `linear-gradient(120deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
          }}
        >
          Identity verification
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ maxWidth: 480 }}>
          Counts and rows are served from the identity-verification service (IDV uploads, dedup, customers).
        </Typography>
      </Stack>

      {summaryQ.isPending ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={36} />
        </Box>
      ) : summaryQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromIdentityVerificationHttpError(summaryQ.error)}
        </Alert>
      ) : (
        <>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CountTile
                label="IDV sessions"
                value={summaryQ.data?.totals?.sessions ?? 0}
                caption="Total identity_verification_sessions rows."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CountTile
                label="Customers"
                value={summaryQ.data?.totals?.customers ?? 0}
                caption="Distinct customers (CCID profiles)."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CountTile
                label="Dedup sessions"
                value={summaryQ.data?.totals?.deduplication_sessions ?? 0}
                caption="deduplication_verification_sessions rows."
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <CountTile
                label="Uploaded images"
                value={summaryQ.data?.totals?.uploaded_images ?? 0}
                caption="Audit rows in identity_verification_uploaded_images."
              />
            </Grid>
          </Grid>

          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Session status
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {sessionStatusEntries.map(([status, count]) => {
                      const selected = statusFilter === status;
                      return (
                        <Chip
                          key={status}
                          size="small"
                          label={`${status} (${count})`}
                          color={statusChipColor(status)}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => {
                            setPage(0);
                            setStatusFilter(selected ? undefined : status);
                          }}
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        />
                      );
                    })}
                    {sessionStatusEntries.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No sessions yet.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Verification route
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {routeEntries.map(([route, count]) => {
                      const selected = routeFilter === route;
                      return (
                        <Chip
                          key={route}
                          size="small"
                          label={`${route} (${count})`}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => {
                            setPage(0);
                            setRouteFilter(selected ? undefined : route);
                          }}
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        />
                      );
                    })}
                    {routeEntries.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No data.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Vendor
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {vendorEntries.map(([vendor, count]) => {
                      const selected = vendorFilter === vendor;
                      return (
                        <Chip
                          key={vendor}
                          size="small"
                          label={`${vendor} (${count})`}
                          color={selected ? 'primary' : 'default'}
                          variant={selected ? 'filled' : 'outlined'}
                          onClick={() => {
                            setPage(0);
                            setVendorFilter(selected ? undefined : vendor);
                          }}
                          sx={{ fontWeight: 700, mb: 0.5 }}
                        />
                      );
                    })}
                    {vendorEntries.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No data.
                      </Typography>
                    ) : null}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, md: 6, lg: 3 }}>
              <Card variant="outlined" sx={{ borderRadius: 2 }}>
                <CardContent>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Dedup status
                  </Typography>
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                    {dedupEntries.map(([status, count]) => (
                      <Chip
                        key={status}
                        size="small"
                        label={`${status} (${count})`}
                        color={statusChipColor(status)}
                        variant="outlined"
                        sx={{ fontWeight: 700, mb: 0.5 }}
                      />
                    ))}
                    {dedupEntries.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">
                        No dedup runs yet.
                      </Typography>
                    ) : null}
                  </Stack>
                  {customerEntries.length > 0 ? (
                    <>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 1.5, mb: 0.75 }}>
                        Customer status
                      </Typography>
                      <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
                        {customerEntries.map(([status, count]) => (
                          <Chip
                            key={status}
                            size="small"
                            label={`${status} (${count})`}
                            color={statusChipColor(status)}
                            variant="outlined"
                            sx={{ fontWeight: 700, mb: 0.5 }}
                          />
                        ))}
                      </Stack>
                    </>
                  ) : null}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </>
      )}

      <Card variant="outlined" sx={{ mb: 2, borderRadius: 2 }}>
        <CardContent>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1.5 }}>
            Filters & sorting
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
            Status / route / vendor chips above stay in sync with this table. Text filters apply after you click Apply
            filters.
          </Typography>
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="iv_verification_id contains"
                value={ivIdQ}
                onChange={(e) => setIvIdQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="internal_id contains"
                value={internalIdQ}
                onChange={(e) => setInternalIdQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="CCID contains"
                value={ccidQ}
                onChange={(e) => setCcidQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Email contains"
                value={emailQ}
                onChange={(e) => setEmailQ(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <TextField
                fullWidth
                size="small"
                label="Phone contains"
                value={phoneQ}
                onChange={(e) => setPhoneQ(e.target.value)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Button size="small" onClick={applySearch}>
                        Apply filters
                      </Button>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 4 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Sort rows by</InputLabel>
                <Select
                  label="Sort rows by"
                  value={sortBy}
                  onChange={(e) => {
                    setPage(0);
                    setSortBy(e.target.value as IdentitySessionSortBy);
                  }}
                >
                  {SORT_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Created after (ISO, e.g. 2026-05-01T00:00:00Z)"
                value={createdFrom}
                onChange={(e) => setCreatedFrom(e.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                fullWidth
                size="small"
                label="Created before (ISO)"
                value={createdTo}
                onChange={(e) => setCreatedTo(e.target.value)}
              />
            </Grid>
          </Grid>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            {(statusFilter || routeFilter || vendorFilter) ? (
              <>
                {statusFilter ? (
                  <Chip
                    size="small"
                    label={`status: ${statusFilter}`}
                    onDelete={() => {
                      setPage(0);
                      setStatusFilter(undefined);
                    }}
                    color="primary"
                  />
                ) : null}
                {routeFilter ? (
                  <Chip
                    size="small"
                    label={`route: ${routeFilter}`}
                    onDelete={() => {
                      setPage(0);
                      setRouteFilter(undefined);
                    }}
                    color="primary"
                  />
                ) : null}
                {vendorFilter ? (
                  <Chip
                    size="small"
                    label={`vendor: ${vendorFilter}`}
                    onDelete={() => {
                      setPage(0);
                      setVendorFilter(undefined);
                    }}
                    color="primary"
                  />
                ) : null}
              </>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      {listQ.isError ? (
        <Alert severity="error" sx={{ mb: 2 }}>
          {messageFromIdentityVerificationHttpError(listQ.error)}
        </Alert>
      ) : null}

      <Card
        variant="outlined"
        sx={{
          borderRadius: 3,
          overflow: 'hidden',
          borderColor: alpha(theme.palette.primary.main, theme.palette.mode === 'light' ? 0.22 : 0.35),
          boxShadow:
            theme.palette.mode === 'light'
              ? `0 12px 40px ${alpha(theme.palette.primary.main, 0.06)}`
              : `0 12px 48px ${alpha('#000', 0.35)}`,
        }}
      >
        <Table size="small" sx={{ '& .MuiTableCell-root': { fontSize: '0.875rem', py: 1.25 } }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: 48 }} aria-label="Open detail" />
              <TableCell>Status</TableCell>
              <TableCell>Route</TableCell>
              <TableCell>Vendor</TableCell>
              <TableCell>iv_verification_id</TableCell>
              <TableCell>internal_id · CCID</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>Document</TableCell>
              <TableCell>Pipeline</TableCell>
              <TableCell>Created</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {listQ.isPending ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <CircularProgress size={32} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : (listQ.data?.items ?? []).length === 0 ? (
              <TableRow>
                <TableCell colSpan={10}>
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                    <Typography variant="body2" color="text.secondary">
                      No identity verification sessions match the current filters.
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              (listQ.data?.items ?? []).map((row: IdentitySessionListItem) => (
                <TableRow key={row.iv_verification_id} hover>
                  <TableCell>
                    <Tooltip title="Open IDV session record (raw vendor data + dedup + customer)">
                      <IconButton
                        size="small"
                        aria-label="Open IDV session detail drawer"
                        onClick={() => setDetailId(row.iv_verification_id)}
                      >
                        <OpenInNewOutlinedIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={row.status}
                      color={statusChipColor(row.status)}
                      sx={{ fontWeight: 700 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.verification_route ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {row.vendor}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                      }}
                    >
                      {row.iv_verification_id}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                        fontSize: '0.78rem',
                        fontWeight: 600,
                      }}
                    >
                      {row.internal_id}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.25 }}>
                      {row.ccid ? `CCID ${row.ccid}` : 'no ccid'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                      {row.email ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8125rem', mt: 0.25 }}>
                      {row.phone_number ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontSize: '0.8125rem' }}>
                      {row.document_type ?? '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.78rem', mt: 0.25 }}>
                      {[row.issuing_country, row.issuing_province].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      <Tooltip title="OCR data captured">
                        <Chip
                          size="small"
                          label="OCR"
                          color={row.has_ocr ? 'success' : 'default'}
                          variant={row.has_ocr ? 'filled' : 'outlined'}
                        />
                      </Tooltip>
                      <Tooltip title="Vendor checks_result present">
                        <Chip
                          size="small"
                          label="Checks"
                          color={row.has_checks ? 'success' : 'default'}
                          variant={row.has_checks ? 'filled' : 'outlined'}
                        />
                      </Tooltip>
                      <Tooltip title="Dedup attempt linked">
                        <Chip
                          size="small"
                          label="Dedup"
                          color={row.has_dedup ? 'success' : 'default'}
                          variant={row.has_dedup ? 'filled' : 'outlined'}
                        />
                      </Tooltip>
                      <Tooltip title={`${row.image_upload_count} image(s) audited`}>
                        <Chip
                          size="small"
                          label={`Imgs ${row.image_upload_count}`}
                          color={row.image_upload_count > 0 ? 'info' : 'default'}
                          variant={row.image_upload_count > 0 ? 'filled' : 'outlined'}
                        />
                      </Tooltip>
                    </Stack>
                  </TableCell>
                  <TableCell sx={{ whiteSpace: 'nowrap' }}>
                    <Typography variant="body2">{formatDateTime(row.created_at)}</Typography>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={listQ.data?.total ?? 0}
          page={page}
          onPageChange={(_, p) => setPage(p)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 25, 50, 100]}
        />
      </Card>

      <Drawer
        anchor="right"
        open={!!detailId}
        onClose={() => setDetailId(null)}
        PaperProps={{ sx: { width: { xs: 1, sm: 560, md: 720 }, maxWidth: '100vw' } }}
      >
        {detailId ? (
          <Box sx={{ p: 2.5, height: '100%', overflow: 'auto' }}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Box>
                <Typography variant="overline" color="text.secondary" sx={{ fontWeight: 800 }}>
                  Identity verification session
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.25 }}>
                  {detailId}
                </Typography>
              </Box>
              <IconButton onClick={() => setDetailId(null)} aria-label="Close detail drawer">
                <CloseRoundedIcon />
              </IconButton>
            </Stack>

            {detailQ.isPending ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                <CircularProgress />
              </Box>
            ) : detailQ.isError ? (
              <Alert severity="error">{messageFromIdentityVerificationHttpError(detailQ.error)}</Alert>
            ) : detailQ.data ? (
              <Stack spacing={2}>
                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Session
                    </Typography>
                    <Grid container spacing={1.5}>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Status
                        </Typography>
                        <Box>
                          <Chip
                            size="small"
                            label={detailQ.data.session.status}
                            color={statusChipColor(detailQ.data.session.status)}
                            sx={{ fontWeight: 700 }}
                          />
                        </Box>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Route
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {detailQ.data.session.verification_route ?? '—'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Vendor
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          {detailQ.data.session.vendor}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Vendor applicant id
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                          {detailQ.data.session.vendor_applicant_id ?? '—'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          internal_id
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                          {detailQ.data.session.internal_id}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          CCID
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-all' }}>
                          {detailQ.data.session.ccid ?? '—'}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Email
                        </Typography>
                        <Typography variant="body2">{detailQ.data.session.email ?? '—'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Phone
                        </Typography>
                        <Typography variant="body2">{detailQ.data.session.phone_number ?? '—'}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Document
                        </Typography>
                        <Typography variant="body2">
                          {detailQ.data.session.document_type ?? '—'}
                          {detailQ.data.session.issuing_country
                            ? ` · ${detailQ.data.session.issuing_country}`
                            : ''}
                          {detailQ.data.session.issuing_province
                            ? ` · ${detailQ.data.session.issuing_province}`
                            : ''}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Issued / Expiry
                        </Typography>
                        <Typography variant="body2">
                          {detailQ.data.session.issued_date ?? '—'}
                          {detailQ.data.session.expiry_date ? ` → ${detailQ.data.session.expiry_date}` : ''}
                        </Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Created
                        </Typography>
                        <Typography variant="body2">{formatDateTime(detailQ.data.session.created_at)}</Typography>
                      </Grid>
                      <Grid size={{ xs: 6 }}>
                        <Typography variant="caption" color="text.secondary">
                          Verified
                        </Typography>
                        <Typography variant="body2">
                          {formatDateTime(detailQ.data.session.verification_date)}
                        </Typography>
                      </Grid>
                    </Grid>
                  </CardContent>
                </Card>

                {detailQ.data.session.ocr_data ? (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        OCR data
                      </Typography>
                      <JsonBlock value={detailQ.data.session.ocr_data} />
                    </CardContent>
                  </Card>
                ) : null}

                {detailQ.data.session.checks_result ? (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Vendor checks_result
                      </Typography>
                      <JsonBlock value={detailQ.data.session.checks_result} />
                    </CardContent>
                  </Card>
                ) : null}

                {detailQ.data.session.vendor_evidence_processing ? (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Vendor evidence processing
                      </Typography>
                      <JsonBlock value={detailQ.data.session.vendor_evidence_processing} />
                    </CardContent>
                  </Card>
                ) : null}

                {detailQ.data.session.raw_idv_response ? (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Raw IDV response
                      </Typography>
                      <JsonBlock value={detailQ.data.session.raw_idv_response} />
                    </CardContent>
                  </Card>
                ) : null}

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Dedup attempts ({detailQ.data.dedupSessions.length})
                    </Typography>
                    {detailQ.data.dedupSessions.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No deduplication runs recorded for this session.
                      </Typography>
                    ) : (
                      <Stack spacing={1.25}>
                        {detailQ.data.dedupSessions.map((d) => (
                          <Card key={d.dv_verification_id} variant="outlined" sx={{ borderRadius: 1.5 }}>
                            <CardContent sx={{ py: 1.25, '&:last-child': { pb: 1.25 } }}>
                              <Stack direction="row" justifyContent="space-between" spacing={1}>
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="body2" sx={{ fontWeight: 700, wordBreak: 'break-all' }}>
                                    {d.dv_verification_id}
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    {formatDateTime(d.created_at)}
                                  </Typography>
                                </Box>
                                <Chip
                                  size="small"
                                  label={d.status}
                                  color={statusChipColor(d.status)}
                                  sx={{ fontWeight: 700, alignSelf: 'flex-start' }}
                                />
                              </Stack>
                              {d.deduplication_result ? (
                                <Box sx={{ mt: 1 }}>
                                  <JsonBlock value={d.deduplication_result} />
                                </Box>
                              ) : null}
                            </CardContent>
                          </Card>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>

                {detailQ.data.customer ? (
                  <Card variant="outlined" sx={{ borderRadius: 2 }}>
                    <CardContent>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                        Linked customer (CCID {detailQ.data.customer.ccid})
                      </Typography>
                      <Grid container spacing={1.5}>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Name
                          </Typography>
                          <Typography variant="body2">
                            {[detailQ.data.customer.first_name, detailQ.data.customer.last_name]
                              .filter(Boolean)
                              .join(' ') || '—'}
                          </Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Status
                          </Typography>
                          <Typography variant="body2">{detailQ.data.customer.status}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Email
                          </Typography>
                          <Typography variant="body2">{detailQ.data.customer.email ?? '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Phone
                          </Typography>
                          <Typography variant="body2">{detailQ.data.customer.phone_number ?? '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            DOB
                          </Typography>
                          <Typography variant="body2">{detailQ.data.customer.date_of_birth ?? '—'}</Typography>
                        </Grid>
                        <Grid size={{ xs: 6 }}>
                          <Typography variant="caption" color="text.secondary">
                            Created
                          </Typography>
                          <Typography variant="body2">{formatDateTime(detailQ.data.customer.created_at)}</Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                          <Typography variant="caption" color="text.secondary">
                            Residential address
                          </Typography>
                          <Typography variant="body2" sx={{ wordBreak: 'break-word' }}>
                            {detailQ.data.customer.residential_address ?? '—'}
                          </Typography>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                ) : null}

                <Card variant="outlined" sx={{ borderRadius: 2 }}>
                  <CardContent>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Uploaded image audit ({detailQ.data.uploadedImages.length})
                    </Typography>
                    {detailQ.data.uploadedImages.length === 0 ? (
                      <Typography variant="body2" color="text.secondary">
                        No audit rows in identity_verification_uploaded_images.
                      </Typography>
                    ) : (
                      <Stack spacing={0.75}>
                        {detailQ.data.uploadedImages.map((img) => (
                          <Box
                            key={img.id}
                            sx={{
                              p: 1,
                              borderRadius: 1.5,
                              border: 1,
                              borderColor: 'divider',
                              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
                              fontSize: '0.78rem',
                            }}
                          >
                            <strong>{img.image_type}</strong> #{img.sequence_index} · {img.content_type} ·{' '}
                            {(img.size_bytes / 1024).toFixed(1)} KB
                            <Box sx={{ color: 'text.secondary', wordBreak: 'break-all', mt: 0.5 }}>
                              sha256: {img.sha256_hash}
                            </Box>
                            <Box sx={{ color: 'text.secondary', wordBreak: 'break-all' }}>path: {img.file_path}</Box>
                          </Box>
                        ))}
                      </Stack>
                    )}
                  </CardContent>
                </Card>
              </Stack>
            ) : null}
          </Box>
        ) : null}
      </Drawer>
    </Box>
  );
}
